import { CaptchaSendType } from '@next-space/fe-api-idl';
import classnames from 'classnames';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { INVITE_CODE_REQUIRED_TEXT, INVITE_CODE_TEXT, VITE_PRODUCT_NAME } from '../../const';
import { useCheckComplete } from '../../hook/use-check-complete';
import { useInitYidunCaptcha } from '../../hook/use-init-yidun-captcha';
import { useLogin } from '../../hook/use-login';
import { Images } from '../../images';
import type { CombineViewProps } from '../../types';
import { AccountStatus, ViewType } from '../../types';
import { Button } from '../../uikit/web/button';
import { CaptchaComp } from '../../uikit/web/captcha';
import { Input } from '../../uikit/web/input';
import { Title } from '../../uikit/web/title';
import { tmpAccount } from '../../utils';
import { cx } from '@flowus/common/cx';

/**验证码登录/注册 */
export const VerifyCodeLogin: FC<CombineViewProps> = (props) => {
  const {
    canSendVerifyCode,
    accountStatus,
    account,
    accountError,
    setAccount,
    verifyCode,
    verifyCodeError,
    setVerifyCode,
    password,
    passwordError,
    setPassword,
    next,
    sendVerifyCode,
    authorization,
    inviteCode,
    setPromotionChannel,
    setInviteCode,
    isPhoneNumber,
    setYidunCaptcha,
  } = useLogin({
    account: sessionStorage.getItem(tmpAccount) ?? undefined,
    ...props,
    newInviteCode: props.inviteCode,
  });
  const checkComplete = useCheckComplete(props.switchViewType);
  const [showPassword, setShowPassword] = useState(false);
  const hideVerifyCodeOrHasVerifyCode = accountStatus === undefined || verifyCode.length > 0;
  const hidePasswordOrHasPassword = accountStatus !== AccountStatus.CAN_SIGN || password.length > 0;
  const allowClick =
    account.length > 0 && hideVerifyCodeOrHasVerifyCode && hidePasswordOrHasPassword;
  const alreadyHasInviteCode = Boolean(props.inviteCode);
  const { initNECaptcha } = useInitYidunCaptcha(setYidunCaptcha);

  const labelInfo = useMemo(() => {
    switch (accountStatus) {
      case AccountStatus.CAN_LOGIN:
        return { title: '👋 欢迎回来', btnText: '登录' };
      case AccountStatus.CAN_SIGN:
        return { title: '🎉 欢迎加入', btnText: '继续' };
      default:
        return {
          title: props.defaultTitle || ['欢迎来到', VITE_PRODUCT_NAME ?? 'FlowUs'].join(''),
          btnText: '继续',
        };
    }
  }, [accountStatus, props.defaultTitle]);

  useEffect(() => {
    if (accountStatus === AccountStatus.NEED_BIND_PHONE) {
      props.switchViewType({
        type: ViewType.BIND_PHONE,
        props: {
          authorization,
          password,
        },
      });
    }
  }, [accountStatus, props, authorization, setPassword, password]);

  useEffect(() => {
    if (accountStatus === AccountStatus.CAN_SIGN) {
      setPassword('');
    }
  }, [accountStatus, setPassword]);

  useEffect(() => {
    setPromotionChannel(props.promotionChannel);
  }, [props.promotionChannel, setPromotionChannel]);

  const tryLogin = async () => {
    const user = await next();
    if (user) {
      props.onRegister?.(user);
    }
    if (user && (!props.complete || checkComplete(user))) {
      props.onSuccess?.(user);
    }
  };

  useEffect(() => {
    if (accountStatus === AccountStatus.CAN_SIGN && isPhoneNumber()) {
      initNECaptcha();
    }
  }, [accountStatus, initNECaptcha, isPhoneNumber]);

  return (
    <div className={classnames('w-full', props.className)}>
      <Title className={cx('mb-7', props.titleClassName)} text={labelInfo.title} />
      <div className="sm:px5">
        <Input
          warning={accountError}
          className="t-input-account w-full h-[50px] h-md:text-h4 px-3"
          inputClassName="px-0"
          autoFocus
          type={props.quickRegister ? 'number' : 'text'}
          maxLength={props.quickRegister ? 11 : undefined}
          onEnter={tryLogin}
          value={account}
          placeholder={props.quickRegister ? '请输入手机号' : '请输入手机号/邮箱'}
          onChange={(v) => {
            setAccount(v.toString());
          }}
        />
        {accountStatus !== undefined && (
          <>
            <div id="yidun-captcha" className="mt-5" />
            <Input
              autoFocus
              type="number"
              value={verifyCode}
              maxLength={6}
              warning={verifyCodeError}
              onEnter={tryLogin}
              className="t-input-verify-code w-full h-[50px] mt-5 h-md:text-h4 px-3 input-verify-code"
              inputClassName="px-0"
              placeholder="请输入验证码"
              onChange={(v) => setVerifyCode(v.toString())}
              addonAfter={
                <CaptchaComp
                  //未注册且图形验证码通过之后才允许发手机验证码，否则就是disable
                  disable={!canSendVerifyCode}
                  autoClick={canSendVerifyCode}
                  onSendVerifyCode={() => {
                    return sendVerifyCode(
                      accountStatus === AccountStatus.CAN_SIGN
                        ? CaptchaSendType.SIGNUP
                        : CaptchaSendType.LOGIN
                    );
                  }}
                />
              }
            />
          </>
        )}
        {accountStatus === AccountStatus.CAN_SIGN && (
          <>
            <Input
              inputType={showPassword ? 'text' : 'password'}
              warning={passwordError}
              className="t-input-password w-full h-[50px] mt-5 h-md:text-h4 px-3 input-password"
              onEnter={tryLogin}
              value={password}
              placeholder="请输入密码"
              onChange={setPassword}
              addonAfter={
                <img
                  src={showPassword ? Images.IcMenuItemHide : Images.IcMenuItemShow}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPassword(!showPassword);
                  }}
                  className="w-5 h-5 cursor-pointer absolute right-2.5 text-grey3"
                />
              }
            />
            <div className="text-t2 text-grey3 mt-2.5">
              6-16位密码，需包含数字、英文大写字母和小写字母
            </div>
            {isPhoneNumber() && (
              <Input
                readonly={alreadyHasInviteCode && !__PRIVATE__}
                value={inviteCode}
                maxLength={6}
                onEnter={tryLogin}
                className={cx(
                  't-input-invite-code w-full h-[50px] mt-5 h-md:text-h4 px-3',
                  props.inviteCodeRequired && '!border-red'
                )}
                inputClassName={cx('px-0', props.inviteCodeRequired && 'placeholder:text-red')}
                placeholder={
                  props.inviteCodeRequired ? INVITE_CODE_REQUIRED_TEXT : INVITE_CODE_TEXT
                }
                onChange={setInviteCode}
              />
            )}
          </>
        )}
        <Button
          disable={!allowClick}
          onClick={tryLogin}
          colorType="primary"
          className={classnames('t-btn-login mt-7 w-full h-[50px] text-t1-medium h-md:text-h4', {
            'opacity-[0.3]': !allowClick,
          })}
          size="auto"
        >
          {labelInfo.btnText}
        </Button>
      </div>
    </div>
  );
};
